import type { FieldLookup, PropertyPath } from "@ngt-form/core";
import { useMemo } from "react";

import type { LookupOption, ModelMetadata } from "src/api/Tmd.dtos";
import convertPascalToCamelCasePropertyPath from "src/utilities/convertPascalToCamelCasePropertyPath";

/**
 * Combines metadata lookup options with caller-provided lookups, memoized by input references.
 *
 * Postconditions: Nonempty option lists retain their original references and order under camel-case
 * field paths. Supplied lookups win. Without metadata fields, returns the supplied dictionary directly
 * or an empty dictionary. No active-option filtering or mutation occurs here.
 * Assumptions: Relevant changes replace metadata/lookup references to invalidate the memo.
 */
const useTMDFormLookups = (
    metadata?: ModelMetadata | null | undefined,
    lookups?: Record<PropertyPath, FieldLookup<any, any>> | null | undefined
): Record<PropertyPath, FieldLookup<any, any>> => {
    return useMemo(
        () => {
            const hasMetadata = metadata && metadata.fields && metadata.fields.length !== 0;

            if (!hasMetadata && !lookups) {
                return {};
            }

            if (!hasMetadata) {
                return lookups;
            }

            const output: Record<PropertyPath, FieldLookup<any, LookupOption>> = {};

            for (let field of metadata.fields) {
                if (!field.lookupOptions || field.lookupOptions.length === 0) {
                    continue;
                }

                const propertyPath = convertPascalToCamelCasePropertyPath(field.propertyPath);
                output[propertyPath] = {
                    getLabel: item => item.label,
                    getValue: item => item.value,
                    items: field.lookupOptions
                };
            }

            return {
                ...output,
                ...lookups
            };
        },
        [metadata, lookups]
    );
};

export default useTMDFormLookups;
